import { View, Text, TextInput, Pressable } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useState, useCallback } from 'react';
import { LegendList } from '@legendapp/list';
import { Query } from '@/utils/react-query/Queries';
import { Mutation } from '@/utils/react-query/Mutations';
import Avatar from '@/components/People/Avatar';
import SolidButton from '@/components/General/SolidButton';
import EmptyListPlaceholder from '@/components/General/EmptyListPlaceholder';
import Unicon from '@/components/General/Unicon';
import { parseTimeFrame } from '@/utils/dater';
import { showAlert } from '@/utils/showAlert';
import Toast from 'react-native-toast-message';
import SearchBox from '@/components/General/inputs/SearchBox';
import SelectInput from '@/components/General/inputs/SelectInput';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import { Tooltip } from '@/components/General/Tooltip';
import { NOTE_VISIBILITY_OPTIONS } from '@/utils/constants/followUp';
import { useRouter } from 'expo-router';

// Canonical visibility for pre-migration notes that only carry isPrivate.
const noteVisibilityOf = (note) =>
    note?.visibility || (note?.isPrivate ? 'PRIVATE' : 'PUBLIC');

const NotesTab = ({ personID, loggedInUser, loggedInUserIsAdmin }) => {
    const router = useRouter();
    const [searchText, setSearchText] = useState('');
    const [formVisible, setFormVisible] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [noteContent, setNoteContent] = useState('');
    const [noteVisibility, setNoteVisibility] = useState('PUBLIC');

    const { data: notes = [] } = Query.peopleNotes.byPersonId({ personID });

    const saveMutation = Mutation.peopleNotes.save({
        onSuccess: () => {
            Toast.show({ type: 'success', text2: editingNote ? 'Note updated' : 'Note added' });
            setFormVisible(false);
            setEditingNote(null);
            setNoteContent('');
            setNoteVisibility('PUBLIC');
        },
        onError: () => {
            Toast.show({ type: 'error', text2: 'Failed to save note' });
        },
    });

    const deleteMutation = Mutation.peopleNotes.delete({
        onSuccess: () => Toast.show({ type: 'success', text2: 'Note deleted' }),
        onError: () => Toast.show({ type: 'error', text2: 'Failed to delete note' }),
    });

    const canEdit = (note) =>
        note.createdBy === loggedInUser?.id || loggedInUserIsAdmin;

    const canAddNote = loggedInUser?.can?.('Add People Notes') || loggedInUserIsAdmin;
    // FOLLOW_UP notes are only offered to Follow Up team members (spec §8.3).
    const canSeeFollowUp = loggedInUser?.can?.('View Follow Up') || loggedInUserIsAdmin;
    const visibilityOptions = NOTE_VISIBILITY_OPTIONS.filter(
        (o) => o.value !== 'FOLLOW_UP' || canSeeFollowUp
    );
    // A BIBLE_STUDY note being edited keeps its locked scope — display it as a
    // fixed option rather than letting the picker blank or re-scope the row.
    if (editingNote && noteVisibilityOf(editingNote) === 'BIBLE_STUDY') {
        visibilityOptions.push({ value: 'BIBLE_STUDY', label: 'Bible Study', subtitle: "Visible to the study's teachers and Bible Study managers" });
    }

    const handleSave = () => {
        if (!noteContent.trim()) return;
        saveMutation.mutate({
            id: editingNote?.id,
            personID,
            content: noteContent.trim(),
            visibility: noteVisibility,
            isPrivate: noteVisibility !== 'PUBLIC',
            createdBy: loggedInUser?.id,
        });
    };

    const handleDelete = (note) => {
        showAlert({
            title: 'Delete note?',
            subtitle: 'This cannot be undone.',
            isDestructive: true,
            confirmText: 'Delete',
            onConfirm: () => deleteMutation.mutate({ id: note.id, personID }),
        });
    };

    const handleEdit = (note) => {
        setEditingNote(note);
        setNoteContent(note.content);
        setNoteVisibility(noteVisibilityOf(note));
        setFormVisible(true);
    };

    const handleCancel = () => {
        setFormVisible(false);
        setEditingNote(null);
        setNoteContent('');
        setNoteVisibility('PUBLIC');
    };

    const filteredNotes = notes.filter(note =>
        note.content.toLowerCase().includes(searchText.toLowerCase())
    );

    const renderItem = useCallback(({ item }) => {
        const showMenu = canEdit(item);
        const isEdited = item.updatedAt !== item.createdAt;

        return (
            <View style={styles.noteCard}>
                <View style={styles.noteHeader}>
                    <Text style={styles.noteContent} selectable>
                        {item.content}
                    </Text>

                    {showMenu && (
                        <DropdownMenu
                            options={[
                                {
                                    name: 'Edit',
                                    icon: dropdownIcons.edit,
                                    onSelect: () => handleEdit(item),
                                },
                                {
                                    name: 'Delete',
                                    icon: dropdownIcons.delete,
                                    destructive: true,
                                    onSelect: () => handleDelete(item),
                                },
                            ]}
                        />
                    )}
                </View>
                {isEdited && (
                    <Text style={styles.editedIndicator}>Edited</Text>
                )}
                <View style={styles.cardFooter}>
                    <View style={styles.authorRow}>
                        <Avatar
                            profile={item.Author}
                            size={22}
                            style={styles.avatar}
                        />
                        <Text style={styles.authorName}>
                            {item.Author?.firstName} {item.Author?.lastName}
                        </Text>
                        <Tooltip title={new Date(item.createdAt).toLocaleString()}>
                            <Text style={styles.timestamp}>
                                {parseTimeFrame(item.createdAt)}
                            </Text>
                        </Tooltip>
                    </View>
                    {noteVisibilityOf(item) === 'PRIVATE' && (
                        <Tooltip title="Only you and admins can see this note">
                            <View style={styles.privateBadge}>
                                <Unicon name="lock-closed" size={10} colorVariant="warning" />
                                <Text style={styles.privateBadgeText}>Private</Text>
                            </View>
                        </Tooltip>
                    )}
                    {noteVisibilityOf(item) === 'FOLLOW_UP' && (
                        <Tooltip title="Visible to the Follow Up care team">
                            <View style={styles.followUpBadge}>
                                <Unicon name="user-round-arrow-left" size={12} colorVariant="paths" />
                                <Text style={styles.followUpBadgeText}>Follow Up team</Text>
                            </View>
                        </Tooltip>
                    )}
                    {noteVisibilityOf(item) === 'BIBLE_STUDY' && (
                        <Tooltip title="Visible to this study's teachers and Bible Study managers">
                            <Pressable
                                testID="bible-study-note-badge"
                                onPress={() => item.smallGroupID && router.push(`/biblestudies/${item.smallGroupID}`)}
                                style={styles.bibleStudyBadge}
                            >
                                <Unicon name="book" size={12} colorVariant="groups" />
                                <Text style={styles.bibleStudyBadgeText}>Bible Study</Text>
                            </Pressable>
                        </Tooltip>
                    )}
                </View>
            </View>
        );
    }, [canEdit]);

    return (
        <View style={styles.container}>
            {/* Toolbar */}
            <View style={styles.toolbar}>
                <View style={styles.searchContainer}>
                    <SearchBox
                        searchText={searchText}
                        placeholder="Search notes..."
                        onChangeText={setSearchText}
                        textChangeDelay={0}
                    />
                </View>
                {canAddNote && (
                    <SolidButton
                        size="sm"
                        iconName="add"
                        text="Add Note"
                        onPress={() => setFormVisible(true)}
                    />
                )}
            </View>

            {/* Compose/Edit Form */}
            {formVisible && (
                <View style={styles.formContainer}>
                    <TextInput
                        style={styles.noteInput}
                        multiline
                        numberOfLines={4}
                        placeholder="Write a note..."
                        placeholderTextColor="#999"
                        value={noteContent}
                        onChangeText={setNoteContent}
                        autoFocus
                    />
                    <View style={styles.divider} />
                    <SelectInput
                        title="Who can see this note?"
                        value={noteVisibility}
                        options={visibilityOptions}
                        hideClearButton
                        hideSelectedPills
                        onUpdate={(v) => setNoteVisibility(v || 'PUBLIC')}
                    />
                    <View style={styles.formActions}>
                        <SolidButton
                            text="Cancel"
                            variant="outlined"
                            type="textSecondary"
                            onPress={handleCancel}
                            width={80}
                        />
                        <SolidButton
                            text="Save"
                            onPress={handleSave}
                            disabled={!noteContent.trim() || saveMutation.isPending}
                            width={80}
                        />
                    </View>
                </View>
            )}

            {/* Notes List */}
            <LegendList
                recycleItems
                keyboardShouldPersistTaps="handled"
                data={filteredNotes}
                renderItem={renderItem}
                estimatedItemSize={300}
                contentContainerStyle={{paddingHorizontal: 12, paddingBottom: 100, paddingTop:2}}
                ListEmptyComponent={
                    <EmptyListPlaceholder
                        message={searchText ? `No notes matching "${searchText}"` : 'No notes yet'}
/>
                }
/>
        </View>
    );
};

const styles = StyleSheet.create((theme) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    toolbar: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 12,
        gap: 12,
        backgroundColor: theme.colors.background,
    },
    searchContainer: {
        flex: 1,
    },
    formContainer: {
        backgroundColor: theme.colors.surface,
        padding: 16,
        marginHorizontal: 12,
        marginBottom: 16,
        borderRadius: 12,
        // borderWidth: 1,
        // borderColor: theme.colors.border,
    },
    noteInput: {
        fontSize: 14,
        color: theme.colors.text,
        textAlignVertical: 'top',
        minHeight: 80,
        outlineStyle: 'none',
        _web: {
            outlineStyle: 'none',
        },
    },
    divider: {
        height: 1,
        backgroundColor: theme.colors.border,
        marginVertical: 12,
    },
    privateToggle: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 8,
    },
    privateToggleTextContainer: {
        flex: 1,
    },
    privateToggleLabel: {
        fontSize: 14,
        color: theme.colors.text,
        fontFamily: theme.fonts.bold,
    },
    privateToggleSubtitle: {
        fontSize: 12,
        color: theme.colors.textSecondary,
        marginTop: 2,
    },
    formActions: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        marginTop: 12,
        gap: 8,
    },
    noteCard: {
        backgroundColor: theme.colors.surface,
        borderRadius: 12,
        boxShadow: theme.shadows.card,
        // borderWidth: 1,
        // borderColor: theme.colors.border,
        padding: 14,
        marginBottom: 10,
    },
    noteHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    cardFooter: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
    },
    authorRow: {
        flexDirection: 'row',
        gap: 8,
        alignItems: 'center',
    },
    authorName: {
        fontSize: 13,
        fontFamily: theme.fonts.bold,
        color: theme.colors.text,
    },
    privateBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        backgroundColor: theme.colors.warningContainer,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 4,
    },
    privateBadgeText: {
        fontSize: 10,
        color: theme.colors.warning,
    },
    followUpBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        backgroundColor: theme.colors.pathsContainer,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 4,
    },
    followUpBadgeText: {
        fontSize: 10,
        color: theme.colors.paths,
    },
    bibleStudyBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        backgroundColor: theme.colors.groupsContainer,
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 4,
    },
    bibleStudyBadgeText: {
        fontSize: 10,
        color: theme.colors.groups,
    },
    timestamp: {
        fontSize: 11,
        color: theme.colors.textSecondary,
    },
    noteContent: {
        flex:1,
        fontSize: 14,
        color: theme.colors.text,
        lineHeight: 22,
    },
    editedIndicator: {
        fontSize: 11,
        color: theme.colors.textSecondary,
        marginTop: 4,
    },
}));

export default NotesTab;
